import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaEnvelope, FaArrowLeft, FaPaperPlane } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { authService } from '../../services/authService';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      const response = await authService.forgotPassword(email);
      if (response.success) {
        toast.success('Reset link sent to your email!');
        setSubmitted(true);
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      toast.error(error.response?.data?.message || 'Failed to send password reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 py-12 px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full space-y-8 bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8"
      >
        <AnimatePresence mode="wait">
          {!submitted ? (
            <motion.div
              key="forgot-form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-6"
            >
              {/* Back to Login link */}
              <div>
                <Link
                  to="/admin/secure-login"
                  className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 gap-2 transition"
                >
                  <FaArrowLeft className="text-xs" /> Back to Sign In
                </Link>
              </div>

              {/* Logo / Title */}
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">
                  <span className="gradient-text">Forgot</span>
                  <span className="text-gray-800 dark:text-white"> Password?</span>
                </div>
                <p className="text-gray-600 dark:text-gray-400">
                  Enter your email address and we'll send you a secure link to reset your password.
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaEnvelope className="text-gray-400" />
                    </div>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="input-field pl-10"
                      placeholder="admin@example.com"
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    'Send Reset Link'
                  )}
                </button>
              </form>
            </motion.div>
          ) : (
            <motion.div
              key="success-message"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, damping: 20 }}
              className="text-center space-y-6 py-4"
            >
              {/* Paper Plane Icon */}
              <div className="flex justify-center">
                <div className="w-20 h-20 bg-primary-50 dark:bg-primary-950/30 text-primary-500 dark:text-primary-400 rounded-full flex items-center justify-center text-3xl shadow-inner border border-primary-100 dark:border-primary-900/50">
                  <motion.div
                    animate={{
                      y: [0, -4, 0],
                      x: [0, 4, 0]
                    }}
                    transition={{
                      repeat: Infinity,
                      duration: 2.5,
                      ease: 'easeInOut'
                    }}
                  >
                    <FaPaperPlane />
                  </motion.div>
                </div>
              </div>

              {/* Success Content */}
              <div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Check your email</h3>
                <p className="text-gray-600 dark:text-gray-400 max-w-sm mx-auto">
                  We've sent a secure password reset link to <strong className="text-gray-800 dark:text-gray-200">{email}</strong>. Please check your inbox and spam folder.
                </p>
              </div>

              {/* Back to Login Button */}
              <div>
                <Link
                  to="/admin/secure-login"
                  className="btn-primary inline-flex items-center justify-center gap-2 w-full max-w-xs"
                >
                  Return to Sign In
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default ForgotPassword;

